"use client";

import { useTheme } from "@/app/lib/context/ThemeContext";
import { cn } from "@/app/lib/utils/cn";

type ThemeToggleVariant = "default" | "mobile" | "header-mobile" | "sidebar";

interface ThemeToggleProps {
  variant?: ThemeToggleVariant;
}

const SunIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
  </svg>
);

const MoonIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
  </svg>
);


const variantStyles = {
  default: {
    button: "w-11 h-11 rounded-full bg-(--bg-card) border-2 border-(--border-color-strong) hover:border-(--teal) shadow-sm focus:outline-none focus:ring-2 focus:ring-(--teal)",
    icon: "w-6 h-6",
    skeleton: "w-11 h-11 rounded-full bg-(--bg-card) border-2 border-(--border-color-strong) shadow-sm",
    skeletonIcon: "w-6 h-6",
    showTooltip: true,
    tooltipPosition: "bottom" as const,
    inlineStyle: undefined as React.CSSProperties | undefined,
  },
  mobile: {
    button: "flex-shrink-0 min-w-[44px] h-[44px] px-3 rounded-full bg-(--purple)/80 hover:bg-(--purple) focus-visible:ring-2 focus-visible:ring-(--gold)",
    icon: "w-5 h-5",
    skeleton: "flex-shrink-0 min-w-[44px] h-[44px] rounded-full bg-(--purple-accent)/30",
    skeletonIcon: "",
    showTooltip: false,
    tooltipPosition: "bottom" as const,
    inlineStyle: undefined as React.CSSProperties | undefined,
  },
  "header-mobile": {
    button: "w-11 h-11 rounded-full bg-(--bg-card) border border-(--border-color-strong) hover:border-(--teal) shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2",
    icon: "w-5 h-5",
    skeleton: "w-11 h-11 rounded-full bg-(--bg-card) border border-(--border-color-strong) shadow-sm",
    skeletonIcon: "w-5 h-5",
    showTooltip: false,
    tooltipPosition: "bottom" as const,
    inlineStyle: undefined as React.CSSProperties | undefined,
  },
  sidebar: {
    button: "rounded-full hover:scale-110 focus-visible:ring-2 focus-visible:ring-(--gold) focus-visible:ring-offset-2 focus-visible:ring-offset-(--bg-card)",
    icon: "w-5 h-5",
    skeleton: "rounded-full bg-(--purple-accent)/30",
    skeletonIcon: "",
    showTooltip: true,
    tooltipPosition: "right" as const,
    inlineStyle: { width: '35px', height: '35px', minWidth: '35px', minHeight: '35px' } as React.CSSProperties,
  },
};

export default function ThemeToggle({ variant = "default" }: ThemeToggleProps) {
  const { theme, mounted, toggleTheme } = useTheme();
  const styles = variantStyles[variant];

  const nextTheme = theme === "light" ? "dark" : "light";

  const tooltipText = nextTheme === "light"
    ? (variant === "sidebar" ? "Switch to Light" : "Switch to Light Mode")
    : (variant === "sidebar" ? "Switch to Dark" : "Switch to Dark Mode");

  if (!mounted) {
    return (
      <div className={cn("relative", variant === "default" && "group")}>
        <button
          type="button"
          className={cn(styles.skeleton, "flex items-center justify-center")}
          style={styles.inlineStyle}
          aria-label="Toggle theme"
        >
          {styles.skeletonIcon && <div className={styles.skeletonIcon} />}
        </button>
      </div>
    );
  }

  const renderIcon = () => {
    const baseClass = styles.icon;
    
    if (theme === "light") {
      return <MoonIcon className={cn(baseClass, "text-(--brand)")} />;
    }
    return <SunIcon className={cn(baseClass, "text-(--gold)")} />;
  };

  const buttonBgClass = theme === "light" 
    ? "bg-(--black) border-(--black)" 
    : "bg-(--white) border-(--white)";

  return (
    <div className={cn("relative", styles.showTooltip && "group")}>
      <button
        type="button"
        onClick={toggleTheme}
        style={styles.inlineStyle}
        className={cn(
          styles.button,
          buttonBgClass,
          "flex items-center justify-center transition-all duration-200 focus:outline-none cursor-pointer"
        )}
        aria-label={tooltipText}
      >
        {renderIcon()}
      </button>
      {styles.showTooltip && (
        <span className={cn(
          "absolute whitespace-nowrap font-medium rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity pointer-events-none",
          styles.tooltipPosition === "bottom" && "bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 text-sm text-(--white) bg-(--purple)",
          styles.tooltipPosition === "right" && "right-full mr-4 top-1/2 -translate-y-1/2 text-base px-4 py-2 rounded-lg bg-(--bg-card) text-(--text-primary) shadow-md border border-(--border-color)"
        )}>
          {tooltipText}
        </span>
      )}
    </div>
  );
}
